import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  rateLimit: number;
  expiresAt?: string;
  lastUsedAt?: string;
  isActive: boolean;
  createdAt: string;
}

interface NewApiKeyResponse {
  id: string;
  name: string;
  key: string;
  keyPrefix: string;
  scopes: string[];
  rateLimit: number;
  expiresAt?: string;
  createdAt: string;
}

export function ApiKeysTab() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState<NewApiKeyResponse | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    scopes: ['read'],
    rateLimit: 1000,
    expiresAt: '',
  });

  useEffect(() => {
    loadApiKeys();
  }, []);

  async function loadApiKeys() {
    try {
      const response = await fetch('/admin/tenant/api-keys', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateKey() {
    try {
      const response = await fetch('/admin/tenant/api-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          name: formData.name,
          scopes: formData.scopes,
          rateLimit: formData.rateLimit,
          expiresAt: formData.expiresAt || undefined,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setNewApiKey(data.data);
        loadApiKeys();
      }
    } catch (error) {
      console.error('Failed to create API key:', error);
    }
  }

  async function handleRevokeKey(keyId: string) {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) return;

    try {
      const response = await fetch(`/admin/tenant/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ reason: 'Manually revoked by admin' }),
      });
      if (response.ok) {
        loadApiKeys();
      }
    } catch (error) {
      console.error('Failed to revoke API key:', error);
    }
  }

  function handleCloseDialog() {
    setIsCreateOpen(false);
    setNewApiKey(null);
    setFormData({ name: '', scopes: ['read'], rateLimit: 1000, expiresAt: '' });
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>API Keys</CardTitle>
            <CardDescription>Manage API keys for programmatic access</CardDescription>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
            <DialogTrigger asChild>
              <Button onClick={() => setIsCreateOpen(true)}>Create API Key</Button>
            </DialogTrigger>
            <DialogContent>
              {newApiKey ? (
                <>
                  <DialogHeader>
                    <DialogTitle>API Key Created</DialogTitle>
                    <DialogDescription>
                      Make sure to copy your API key now. You won't be able to see it again!
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <div className="rounded-lg bg-muted p-4">
                      <div className="mb-2 text-sm font-medium">Your API Key:</div>
                      <code className="break-all text-sm">{newApiKey.key}</code>
                    </div>
                    <Button
                      variant="outline"
                      className="mt-4 w-full"
                      onClick={() => copyToClipboard(newApiKey.key)}
                    >
                      Copy to Clipboard
                    </Button>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleCloseDialog}>Done</Button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  <DialogHeader>
                    <DialogTitle>Create API Key</DialogTitle>
                    <DialogDescription>
                      Create a new API key for accessing the platform programmatically.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="key-name">Name</Label>
                      <Input
                        id="key-name"
                        placeholder="My API Key"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="rate-limit">Rate Limit (requests/hour)</Label>
                      <Input
                        id="rate-limit"
                        type="number"
                        value={formData.rateLimit}
                        onChange={(e) => setFormData({ ...formData, rateLimit: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="expires">Expires At (optional)</Label>
                      <Input
                        id="expires"
                        type="datetime-local"
                        value={formData.expiresAt}
                        onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Scopes</Label>
                      <div className="flex flex-wrap gap-2">
                        {['read', 'write', 'admin'].map((scope) => (
                          <Badge
                            key={scope}
                            variant={formData.scopes.includes(scope) ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => {
                              const scopes = formData.scopes.includes(scope)
                                ? formData.scopes.filter((s) => s !== scope)
                                : [...formData.scopes, scope];
                              setFormData({ ...formData, scopes });
                            }}
                          >
                            {scope}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={handleCloseDialog}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateKey} disabled={!formData.name}>
                      Create Key
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading API keys...</div>
        ) : apiKeys.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            No API keys yet. Create one to get started.
          </div>
        ) : (
          <div className="rounded-md border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Key Prefix</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Scopes</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Last Used</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {apiKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3 font-medium">{key.name}</td>
                    <td className="px-4 py-3">
                      <code className="text-sm">{key.keyPrefix}...</code>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {key.scopes.map((scope) => (
                          <Badge key={scope} variant="secondary">
                            {scope}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {key.isActive ? (
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      ) : (
                        <Badge variant="destructive">Revoked</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {key.lastUsedAt
                        ? new Date(key.lastUsedAt).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {key.isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                          onClick={() => handleRevokeKey(key.id)}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
